import {
    Component,
    ElementRef,
    inject,
    input,
    output,
    viewChild,
    effect,
    signal,
    computed,
    untracked,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { DeliveryNotesService } from 'api/services';
import { lastValueFrom } from 'rxjs';
import {
    TableRowData,
    hasKey,
    hasOrgSiteKey,
} from '@app/shared/models/table-data-types';
import {
    ColumnConfig,
    ColumnValue,
} from '@app/shared/services/view-configs/column-creator';
import { AutoRefreshBlockerService } from '@app/shared/services/auto-refresh-blocker.service';

@Component({
    selector: 'app-reference-number-edit',
    standalone: true,
    imports: [
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        TranslateModule,
    ],
    template: `
        <div class="tw-flex tw-items-center tw-w-full tw-group">
            <!-- Read-only mode -->
            @if (!isEditing()) {
                <!-- Read-only display (no editing allowed) -->
                @if (readOnly()) {
                    <div class="tw-w-full tw-text-gray-700 tw-py-1">
                        {{ displayValue() || '—' }}
                    </div>
                }
                <!-- Editable display -->
                @if (!readOnly()) {
                    <!-- With existing reference -->
                    @if (displayValue()) {
                        <div
                            class="tw-cursor-pointer tw-w-full tw-px-2 tw-py-1 tw-rounded tw-border tw-border-transparent hover:tw-border-blue-200 hover:tw-bg-blue-50 tw-transition-all tw-duration-200 tw-group"
                            (click)="startEditing()"
                            [matTooltip]="
                                'GENERAL.REFERENCE-NUMBER.CLICK-TO-EDIT'
                                    | translate
                            ">
                            <div
                                class="tw-flex tw-items-center tw-justify-between">
                                <span class="tw-text-gray-900">{{
                                    displayValue()
                                }}</span>
                                <mat-icon
                                    class="tw-text-gray-400 group-hover:tw-text-blue-500 tw-transition-colors tw-duration-200 tw-text-sm tw-opacity-70 group-hover:tw-opacity-100"
                                    fontSet="material-symbols-outlined">
                                    edit
                                </mat-icon>
                            </div>
                        </div>
                    }
                    <!-- No reference yet -->
                    @if (!displayValue()) {
                        <div
                            class="tw-cursor-pointer tw-w-full tw-px-2 tw-py-1 tw-rounded tw-border tw-border-transparent hover:tw-border-blue-200 hover:tw-bg-blue-50 tw-transition-all tw-duration-200 tw-group"
                            (click)="startEditing()"
                            [matTooltip]="
                                'GENERAL.REFERENCE-NUMBER.CLICK-TO-ADD'
                                    | translate
                            ">
                            <div
                                class="tw-flex tw-items-center tw-justify-between">
                                <span class="tw-text-gray-500 tw-italic">{{
                                    'OUTGOING-GOODS.FOLDER-VIEW.REFERENCE'
                                        | translate
                                }}</span>
                                <mat-icon
                                    class="tw-text-gray-400 group-hover:tw-text-blue-500 tw-transition-colors tw-duration-200 tw-text-sm tw-opacity-70 group-hover:tw-opacity-100"
                                    fontSet="material-symbols-outlined">
                                    edit
                                </mat-icon>
                            </div>
                        </div>
                    }
                }
            }

            <!-- Edit mode -->
            @if (isEditing()) {
                <div
                    class="tw-flex tw-items-center tw-w-full tw-px-1 tw-py-0.5 tw-rounded tw-border tw-border-blue-200 tw-bg-blue-50 tw-transition-all tw-duration-200">
                    <input
                        #inputRef
                        type="text"
                        class="tw-flex-1 tw-border-none tw-outline-none tw-px-1 tw-py-0 tw-text-sm tw-bg-transparent tw-text-gray-900"
                        [placeholder]="
                            'GENERAL.REFERENCE-NUMBER.ENTER-PLACEHOLDER'
                                | translate
                        "
                        [style.min-width.ch]="8"
                        minlength="3"
                        maxlength="60"
                        [(ngModel)]="editValue"
                        (keydown.enter)="saveReference()"
                        (keydown.escape)="cancelEdit()" />
                    <div class="tw-flex tw-items-center">
                        <button
                            aria-label="save-reference-number"
                            (click)="saveReference()"
                            class="tw-p-0.5 tw-rounded tw-text-green-600 hover:tw-bg-green-100 tw-transition-colors tw-duration-200"
                            mat-icon-button>
                            <mat-icon
                                class="tw-text-lg"
                                fontSet="material-symbols-outlined">
                                save
                            </mat-icon>
                        </button>
                        <button
                            aria-label="cancel-edit-reference"
                            (click)="cancelEdit()"
                            class="tw-p-0.5 tw-rounded tw-text-gray-500 hover:tw-bg-gray-100 tw-transition-colors tw-duration-200"
                            mat-icon-button>
                            <mat-icon
                                class="tw-text-lg"
                                fontSet="material-symbols-outlined">
                                cancel
                            </mat-icon>
                        </button>
                    </div>
                </div>
            }
        </div>
    `,
})
export class ReferenceNumberEditComponent {
    value = input<ColumnValue>('');
    row = input.required<TableRowData>();
    column = input<ColumnConfig>();
    index = input<number>(0);
    item = input<TableRowData>(); // legacy alias
    parentRow = input<TableRowData>(); // for expanded rows
    organizationSiteKey = input<string>('');
    readOnly = input<boolean>(false);
    orgType = input<'consignor' | 'carrier' | 'consignee'>('consignor');
    valueChanged = output<string>();
    refresh = output<void>();

    inputRef = viewChild<ElementRef>('inputRef');

    isEditing = signal(false);
    editValue = '';

    // Local value to display after successful save
    private localValue = signal<string | null>(null);
    displayValue = computed(() => this.localValue() ?? this.value());

    private refreshBlocker = inject(AutoRefreshBlockerService);

    constructor(
        private deliveryNotesService: DeliveryNotesService,
        private toastService: ToastService,
        private translateService: TranslateService
    ) {
        // Watch for row/value changes and cancel edit mode when they change
        effect(() => {
            this.row(); // track row changes
            this.value(); // track value changes
            // Avoid tracking isEditing itself, otherwise entering edit mode
            // immediately retriggers this effect and closes the editor.
            if (untracked(() => this.isEditing())) {
                this.cancelEdit();
            }
        });

        // Pause the table auto-reload while the user is typing here; a reload
        // would swap the row input and cancel the edit, losing the text.
        effect(onCleanup => {
            if (this.isEditing()) {
                const release = this.refreshBlocker.acquire('inline-edit');
                onCleanup(release);
            }
        });
    }

    startEditing(): void {
        if (this.readOnly()) {
            return;
        }

        this.isEditing.set(true);
        // Convert value to string for editing, handling all possible types
        // Use displayValue to get the most current value (including local updates)
        this.editValue = this.displayValue() ? String(this.displayValue()) : '';

        // Focus the input after the view updates
        setTimeout(() => {
            const inputRef = this.inputRef();
            if (inputRef) {
                inputRef.nativeElement.focus();
                inputRef.nativeElement.select();
            }
        });
    }

    cancelEdit(): void {
        this.isEditing.set(false);
        this.editValue = '';
    }

    async saveReference(): Promise<void> {
        try {
            const referenceNumber = this.editValue || '';

            // Validate the reference number before sending the request
            const isValid = /^$|^[^,]{3,60}$/.test(referenceNumber);

            if (!isValid) {
                this.toastService.error(
                    this.translateService.instant(
                        'OUTGOING-GOODS.UPLOAD.ERROR.NOT-MATCHING-CRITERIAS'
                    )
                );
                return;
            }

            // fallback to item for legacy
            const rowData = this.row() || this.item();
            // check parent first, then row
            const parentRow = this.parentRow();
            const siteKey =
                this.organizationSiteKey() ||
                (parentRow && hasOrgSiteKey(parentRow)
                    ? parentRow.orgSiteKey
                    : '') ||
                (hasOrgSiteKey(rowData) ? rowData.orgSiteKey : '');

            // need _key for API call
            if (!hasKey(rowData)) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            // Call the appropriate API based on org type
            if (this.orgType() === 'carrier') {
                await lastValueFrom(
                    this.deliveryNotesService.patchCarrierDeliveryNoteProperties(
                        {
                            organizationSiteKey: siteKey,
                            deliveryNoteKey: rowData._key,
                            body: { referenceNumber },
                        }
                    )
                );
            } else if (this.orgType() === 'consignor') {
                await lastValueFrom(
                    this.deliveryNotesService.patchConsignorDeliveryNoteProperties(
                        {
                            organizationSiteKey: siteKey,
                            deliveryNoteKey: rowData._key,
                            body: { referenceNumber },
                        }
                    )
                );
            } else if (this.orgType() === 'consignee') {
                // Add consignee implementation if needed
            }

            // Close edit mode
            this.isEditing.set(false);
            this.editValue = '';

            // Update local value to display the new reference number immediately
            // (necessary because ngComponentOutlet doesn't support output bindings)
            this.localValue.set(referenceNumber);

            // Emit events (kept for potential future use or other binding methods)
            this.valueChanged.emit(referenceNumber);
            this.refresh.emit();

            this.toastService.success(
                this.translateService.instant(
                    'OUTGOING-GOODS.FOLDER-VIEW.REFERENCE-UPDATE-SUCCESS'
                )
            );
        } catch (error) {
            this.toastService.error(
                this.translateService.instant('GENERAL-ERROR')
            );
        }
    }
}
